import type {
  BikeEquipment,
  RecommendationInput,
  RecommendationResult,
  RecoveryEquipment,
  RecoveryProtocolRecommendation,
  NutritionProtocolRecommendation
} from "../types/garage";

function isActiveAndAvailable(item: { archived: boolean; lifecycleStatus: string; readinessStatus: string }): boolean {
  if (item.archived) {
    return false;
  }

  if (item.lifecycleStatus === "retired" || item.lifecycleStatus === "sold" || item.lifecycleStatus === "planned" || item.lifecycleStatus === "inactive") {
    return false;
  }

  return item.readinessStatus === "ready" || item.readinessStatus === "maintenance-due";
}

function selectBike(input: RecommendationInput): BikeEquipment | null {
  const eligible = input.bikes.filter(bike => isActiveAndAvailable(bike) && bike.rideProfile.includes(input.workout.rideProfile));
  if (eligible.length === 0) {
    return input.bikes.find(bike => isActiveAndAvailable(bike)) ?? null;
  }

  const ready = eligible.find(bike => bike.readinessStatus === "ready");
  return ready ?? eligible[0];
}

function selectRecoveryProtocol(input: RecommendationInput): RecoveryProtocolRecommendation {
  const hotTub = input.recoveryEquipment.find(item => item.type === "hot-tub" && isActiveAndAvailable(item));
  const coldPlunge = input.recoveryEquipment.find(item => item.type === "cold-plunge" && isActiveAndAvailable(item));

  const shouldUseCold = input.workout.intensity === "high" || input.weather.highF >= 85;
  const preferred = shouldUseCold ? coldPlunge : hotTub;

  if (!preferred) {
    return {
      modality: "Mobility",
      target: "15 minutes",
      timing: "Evening"
    };
  }

  return buildRecoveryRecommendation(preferred);
}

function buildRecoveryRecommendation(item: RecoveryEquipment): RecoveryProtocolRecommendation {
  if (item.type === "cold-plunge") {
    return {
      modality: item.name,
      target: `${item.defaultTemperatureF ?? 50}F for ${item.defaultDurationMinutes ?? 5} minutes`,
      timing: item.preferredTiming ?? "morning"
    };
  }

  if (item.type === "hot-tub") {
    return {
      modality: item.name,
      target: `${item.defaultDurationMinutes ?? 10} minutes`,
      timing: item.preferredTiming ?? "evening"
    };
  }

  return {
    modality: item.name,
    target: "Protocol pending",
    timing: item.preferredTiming ?? "evening"
  };
}

function selectNutritionProtocol(input: RecommendationInput): NutritionProtocolRecommendation {
  const carbs = input.athlete.nutritionPreferences.carbTargetPerHour;
  const hydration = input.athlete.nutritionPreferences.hydrationOzPerHour;
  const sodium = input.athlete.nutritionPreferences.sodiumMgPerHour;

  const hydrationBump = input.weather.highF >= 85 ? 4 : 0;
  const sodiumBump = input.weather.highF >= 85 ? 150 : 0;

  return {
    carbsPerHour: `${carbs[0]}-${carbs[1]} g/hr`,
    hydrationPerHour: `${hydration[0] + hydrationBump}-${hydration[1] + hydrationBump} oz/hr`,
    sodiumPerHour: `${sodium[0] + sodiumBump}-${sodium[1] + sodiumBump} mg/hr`
  };
}

function computeConfidence(result: {
  bike: BikeEquipment | null;
  recoveryProtocol: RecoveryProtocolRecommendation;
}): number {
  let score = 0.7;
  if (result.bike?.readinessStatus === "ready") {
    score += 0.12;
  } else if (result.bike?.readinessStatus === "maintenance-due") {
    score -= 0.06;
  }
  if (result.recoveryProtocol.modality !== "Mobility") {
    score += 0.08;
  }

  return Math.max(0, Math.min(1, score));
}

export function buildRecommendation(input: RecommendationInput): RecommendationResult {
  const bike = selectBike(input);
  const recoveryProtocol = selectRecoveryProtocol(input);
  const nutritionProtocol = selectNutritionProtocol(input);

  const reasoning: string[] = [];
  if (bike) {
    reasoning.push(`${bike.nickname} matches ${input.workout.rideProfile} ride profile requirements.`);
    if (bike.readinessStatus === "maintenance-due") {
      reasoning.push("Selected bike is maintenance-due. Schedule service before high-priority race sessions.");
    }
  } else {
    reasoning.push("No active bike matched the current workout profile.");
  }

  reasoning.push(`Weather at ${input.weather.highF}F supports increased hydration and sodium strategy.`);
  reasoning.push(`Recovery preference favors ${recoveryProtocol.modality} at ${recoveryProtocol.timing}.`);

  return {
    recommendedBike: bike,
    recoveryProtocol,
    nutritionProtocol,
    reasoning,
    confidence: computeConfidence({ bike, recoveryProtocol })
  };
}
